/**
 * Loading the texture library for the material editor.
 * @module
 */
import * as THREE from "three";
import THREEAssetLoader from "../THREEAssetLoader";
import { DEFAULT_TEXTURE_ANISOTROPY } from "./designMaterialDefaults";
import {
  heightMapTextureNames,
  maskTextureNames,
  normalMapNames,
  proceduralTextures,
  textureNames,
} from "./textureNames";

export const textureDir = "/static/textures/";

export function urlFromTextureName(textureName: string, lossyWebPSupported: boolean): string {
  if (textureName === undefined) {
    return "";
  }
  if (textureName.startsWith("normalmaps/")) {
    return "/static/textures/" + textureName + ".png";
  }
  if ((maskTextureNames as readonly string[]).includes(textureName)) {
    return "/static/textures-maskonly/" + textureName + ".png";
  }
  if ((heightMapTextureNames as readonly string[]).includes(textureName)) {
    return "/static/textures-heightmap/" + textureName + ".png";
  }
  if (!lossyWebPSupported) {
    return "/static/textures-grayscale/" + textureName + ".png";
  }
  return "/static/textures-grayscale/" + textureName + ".webp";
}

export async function loadAllMaterialEditorTextures() {
  const fetchedTextures: { [name: string]: THREE.Texture } = {};
  async function loadTexture(url: string, overrideFormat?: THREE.PixelFormat) {
    const tex = await THREEAssetLoader.instance.loadTexture(url, false, overrideFormat);
    tex.name = url;
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.anisotropy = DEFAULT_TEXTURE_ANISOTROPY;

    fetchedTextures[url] = tex;
  }

  const lossyWebPSupported = await THREEAssetLoader.setupLossyWebPSupported();

  const texFormat = THREE.RedFormat;

  const normalMapLoads = normalMapNames
    .map((name) => urlFromTextureName(name, lossyWebPSupported))
    .map(async (url) => {
      await loadTexture(url, undefined);
    });
  const layerTextureLoads = textureNames
    .filter((name) => proceduralTextures.indexOf(name) < 0)
    .map((name) => urlFromTextureName(name, lossyWebPSupported))
    .map(async (url) => {
      await loadTexture(url, texFormat);
    });
  const maskTextureLoads = maskTextureNames
    .map((name) => urlFromTextureName(name, lossyWebPSupported))
    .map(async (url) => {
      await loadTexture(url, texFormat);
    });
  const heightMapTextureLoads = heightMapTextureNames
    .map((name) => urlFromTextureName(name, lossyWebPSupported))
    .map(async (url) => {
      await loadTexture(url, texFormat);
    });
  await Promise.all([...normalMapLoads, ...layerTextureLoads, ...maskTextureLoads, ...heightMapTextureLoads]);
  return fetchedTextures;
}
